import * as React from 'react';
import { rgba } from 'polished';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import cogoToast from 'cogo-toast';
import Button from '~/theseus/components/Button';
import SectionToggle from '~/theseus/components/SectionToggle';
import Spinner from '~/theseus/components/Spinner';

import useLeadState from './useLeadState';

import Header from '~/theseus/components/Layout/Header';
import Container from '~/theseus/components/Layout/Container';

import EnabledSwitch from './EnabledSwitch';
import TermsServiceModal from './TermsServiceModal';
import TypeSelector from './TypeSelector';
import SourceSelector from './SourceSelector';
import FormCreator from './FormCreator';
import FormSelector from './FormSelector';
import SelectedForm from './SelectedForm';
import { useListener } from '~/emitter';

const Description = styled.p`
  width: 100%;
  margin-top: 10px;
  font-size: 14px;
`;

const SelectorContainer = styled.div`
  display: flex;
  margin: 20px;

  label {
    margin: auto 10px;
    flex-grow: 2;

    & > span {
      display: block;
      font-size: 1.1em;
      font-weight: 500;
      margin-bottom: 10px;
    }
  }
`;

const ButtonContainer = styled.div`
  border-top: 1px solid ${rgba('#000', 0.1)};
  background: ${rgba('#000', 0.03)};
  box-shadow: inset 0 1px ${rgba('#000', 0.025)};
`;

interface LeadAdProps {
  linkType: LinkType;
  link: string;
  propertyImages?: string[];
  defaultSelectedForm?: string;
}

function LeadAds(props: LeadAdProps) {
  const {
    propertyImages = [], linkType, link, defaultSelectedForm,
  } = props;

  const state = useLeadState(defaultSelectedForm);

  const leadAdRef = React.useRef<HTMLDivElement | null>(null);

  const buttonText = React.useMemo(() => {
    if (state.matches('disabled.needsTos')) {
      return 'Accept Lead Ad Terms of Service';
    }

    return 'Choose or Create a Lead Capture Form';
  }, [state]);

  useListener('needs-lead-ad', () => {
    leadAdRef.current?.scrollIntoView({
      behavior: 'smooth',
    });

    setTimeout(() => {
      cogoToast.error(
        'Please select or create a Lead Ads Capture Form before creating your ad.',
        { hideAfter: 5 },
      );
    }, 500);
  });

  if (state.matches('hidden')) {
    return null;
  }

  return (
    <Container ref={leadAdRef}>
      <Header>
        <h2>
          <span>(Optional) </span>
          Use Facebook Lead Ads Form
        </h2>
        <EnabledSwitch
          isEnabled={state.matches('enabled')}
          label="Enable/Disable lead ads"
          onClick={state.toggleEnabled}
          disabled={['disabled.needsTos', 'enabled.loadingFormData'].some(
            state.matches,
          )}
        />
        <Description>
          Facebook Lead Ads capture a lead&apos;s contact information directly
          from their Facebook account.
        </Description>
      </Header>
      <SelectorContainer>
        <label htmlFor="lead-type-select">
          <span>Lead Type</span>
          <TypeSelector
            aria-label="Lead Type"
            innerId="lead-type-select"
            placeholder="Select or type in lead type..."
            isDisabled={state.matches('disabled')}
            value={state.type}
            subscriptionRef={state.subscriptionRef}
            onChange={state.changeType}
          />
        </label>
        <label htmlFor="lead-source-select">
          <span>Lead Source</span>
          <SourceSelector
            aria-label="Lead Source"
            innerId="lead-source-select"
            placeholder="Select or type in lead source..."
            isDisabled={state.matches('disabled')}
            value={state.source}
            onChange={state.changeSource}
          />
        </label>
      </SelectorContainer>
      <SectionToggle disabledOn={state.matches('enabled')}>
        <ButtonContainer>
          <Button
            css={css`
              margin: 20px;
            `}
            variant="secondary"
            size="lg"
            disabled={['disabled.checkingTosAccepted', 'initializing'].some(state.matches)}
            onClick={
              state.matches('disabled.needsTos')
                ? state.openTosModal
                : state.toggleEnabled
            }
          >
            {buttonText}
          </Button>
        </ButtonContainer>
      </SectionToggle>
      <SectionToggle enabledOn={state.matches('disabled.needsTos')}>
        {state.termsService ? (
          <TermsServiceModal termsRef={state.termsService} />
        ) : null}
      </SectionToggle>
      <SectionToggle enabledOn={state.matches('enabled')}>
        <div
          css={css`
            padding: 20px;
            border-top: 1px solid ${rgba('#000', 0.1)};
          `}
        >
          <SectionToggle enabledOn={state.matches('enabled.loadingFormData')}>
            <div
              css={css`
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                padding: 20px;
              `}
            >
              <Spinner
                radius={40}
                css={css`
                  margin-bottom: 30px;
                `}
              />
              <p>Loading your previously created lead ad forms...</p>
            </div>
          </SectionToggle>
          <SectionToggle enabledOn={state.matches('enabled.create')}>
            <FormCreator
              showBackButton={!state.matches('enabled.create.first')}
              linkType={linkType}
              link={link}
              propertyImages={propertyImages}
              onToggleModes={state.toggleModes}
              onCreated={state.selectCreated}
            />
          </SectionToggle>
          <SectionToggle enabledOn={state.matches('enabled.select')}>
            {state.forms && (
              <FormSelector
                forms={state.forms}
                onToggleModes={state.toggleModes}
                onChange={state.selectForm}
              />
            )}
          </SectionToggle>
          <SectionToggle enabledOn={state.matches('enabled.formSelected')}>
            {/* selectedForm is guarenteed to be a string at this point */}
            {state.forms && (
              <SelectedForm
                formId={state.selectedForm as string}
                onReset={state.resetSelected}
                forms={state.forms}
              />
            )}
          </SectionToggle>
        </div>
      </SectionToggle>
    </Container>
  );
}

export default LeadAds;
